/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useReducer, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { UiController } from '..';
import { getCommandRoute } from '../utils/command-router';

/**
 * Custom hook to forward the generic commands to the tab of the command url.
 * It keeps each received command value and, once the active tab is the tab of the command url,
 * forwards it to the setter of that tab, see getCommandRoute for a route entry.
 * @param {Array} routeEntries - route entries in the order they are forwarded, should be a constant
 * @param {Number} tabValue - active tab value
 * @param {Number} urlTab - tab value of the command url
 * @param {Object} tabList - tab name to tab value
 */
const useCommandRouter = (routeEntries, tabValue, urlTab, tabList) => {
  const dispatch = useDispatch();
  // received command values that are not cleared yet, as event to { value, forwardedTo }
  const heldRef = useRef({});
  const [received, notifyReceived] = useReducer((count) => count + 1, 0);

  // Add listener functions to keep the value of each routed command
  useEffect(() => {
    const listeners = routeEntries.map((entry) => {
      const listener = (value) => {
        heldRef.current[entry.event] = { value, forwardedTo: null };
        notifyReceived();
      };
      UiController.subscribe(entry.event, listener);
      return [entry.event, listener];
    });
    return () => {
      listeners.forEach(([event, listener]) => UiController.unsubscribe(event, listener));
    };
  }, [routeEntries]);

  // Triggers when a command value is received, tab value or tab of the command url changes
  useEffect(() => {
    if (tabValue !== urlTab) {
      // values that are kept are forwarded again the next time this tab is the tab of a command url
      Object.values(heldRef.current).forEach((held) => {
        held.forwardedTo = null;
      });
      return;
    }
    routeEntries.forEach((entry) => {
      const held = heldRef.current[entry.event];
      // a value that is kept is forwarded once while the tab is the tab of the command url
      if (!held || held.value === null || held.forwardedTo === tabValue) {
        return;
      }
      const { route, clear } = getCommandRoute(entry, tabValue, tabList);
      if (typeof route === 'function') {
        route(held.value, dispatch);
      } else if (route) {
        UiController[route](entry.value ? entry.value(held.value) : held.value);
      }
      if (clear) {
        delete heldRef.current[entry.event];
      } else {
        held.forwardedTo = tabValue;
      }
    });
  }, [received, tabValue, urlTab, routeEntries, tabList, dispatch]);
};

export default useCommandRouter;
